import i18n from '@/assets/i18n/i18n';
import FormModal from '@/components/common/Modal/FormModal';
import { getAlbumStateBadgeClass } from '@/components/common/ProductUtilities';
import TextInput from '@/components/common/TextInput';
import { useErrorContext } from '@/context/ErrorContext';
import { ITrack, ITrackLookupResult } from '@/data/models/track';
import { getCmsProductUrl } from '@/helpers/productUrls';
import { translatePhysicalProductType } from '@/helpers/translateProductType';
import useGetGenresQuery from '@/hooks/useGetGenresQuery';
import useLookupTracksQuery from '@/hooks/useLookupTracksQuery';
import useUpdateTrackMutation from '@/hooks/useUpdateTrackMutation';
import { ColDef, SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import copyLookupMetadataToTrack from './copyLookupMetadataToTrack';
import styles from './LookupTrackModal.module.scss';

type Props = {
  show: boolean;
  onClose: () => void;
  source: 'digital' | 'physical';
  track: ITrack;
  targetFormatName?: string | null;
  labelId?: string;
  reloadTracks?: () => Promise<void>;
};

const DEFAULT_COL_DEF: ColDef<ITrackLookupResult> = {
  suppressHeaderMenuButton: true,
  resizable: true,
  sortable: true,
};

const FIT_GRID_WIDTH_AUTO_SIZE_STRATEGY = {
  type: 'fitGridWidth',
} as const;

export default function LookupTrackModal({
  show,
  onClose,
  source,
  track,
  targetFormatName,
  labelId,
  reloadTracks,
}: Props) {
  const { t } = useTranslation();
  const gridRef = useRef<AgGridReact<ITrackLookupResult>>(null);
  const [isrc, setIsrc] = useState('');
  const [searchIsrc, setSearchIsrc] = useState('');
  const [selectedLookupResult, setSelectedLookupResult] =
    useState<ITrackLookupResult | null>(null);

  const { data: lookupResults, isFetching } = useLookupTracksQuery(
    searchIsrc,
    source,
    labelId
  );

  const { data: genres = [], isFetching: isFetchingGenres } =
    useGetGenresQuery();
  const updateTrackMutation = useUpdateTrackMutation();
  const { setError } = useErrorContext();
  const columnDefs = useColumnDefs();

  const prsGenres = useMemo(() => genres.map((genre) => genre.value), [genres]);

  const visibleLookupResults = useMemo(
    () => filterCurrentTrackFromLookupResults(lookupResults, track),
    [lookupResults, track]
  );
  const trimmedIsrc = isrc.trim();
  const hasSearched = searchIsrc.trim().length > 0;
  const searchMatchesInput = trimmedIsrc === searchIsrc;
  const noTracksFound =
    hasSearched && !isFetching && visibleLookupResults.length === 0;
  const isGridEmpty = !isFetching && visibleLookupResults.length === 0;

  const loadingOverlayTemplate = useMemo(
    () => renderGridOverlay(t('common.generic.loading'), 'loading'),
    [t]
  );

  const noRowsOverlayTemplate = useMemo(
    () =>
      noTracksFound
        ? renderGridOverlay(t('track.lookup.noTracksFound'), 'no-rows')
        : renderGridOverlay('', 'no-rows'),
    [noTracksFound, t]
  );

  const titleText =
    source === 'digital'
      ? t('track.getTrackFromDigital')
      : t('track.getTrackFromPhysical');

  const isWaitingForGenreCatalog =
    source === 'digital' && !!selectedLookupResult?.genre && isFetchingGenres;

  const submitButtonDisabled =
    !selectedLookupResult ||
    !searchMatchesInput ||
    updateTrackMutation.isPending ||
    isWaitingForGenreCatalog;

  useEffect(() => {
    const gridApi = gridRef.current?.api;

    if (!gridApi) return;

    gridApi.setGridOption('loading', isFetching);

    if (isFetching) return;

    if (noTracksFound) {
      gridApi.showNoRowsOverlay();
      return;
    }

    gridApi.hideOverlay();
  }, [isFetching, noTracksFound]);

  const submit = () => {
    if (!selectedLookupResult || !track.id) return;

    const updatedTrack = copyLookupMetadataToTrack(
      track,
      selectedLookupResult,
      prsGenres,
      targetFormatName
    );

    updateTrackMutation.mutate(
      { trackId: track.id, track: updatedTrack },
      {
        onSuccess: async () => {
          await reloadTracks?.();
          onClose();
        },
        onError: () => setError(t('common.error.defaultError')),
      }
    );
  };

  const handleSearch = () => {
    setSearchIsrc(trimmedIsrc);
    setSelectedLookupResult(null);
  };

  const handleSelectionChanged = (
    event: SelectionChangedEvent<ITrackLookupResult>
  ) => {
    const [lookupResult] = event.api.getSelectedRows();
    setSelectedLookupResult(lookupResult ?? null);
  };

  return (
    <FormModal
      show={show}
      onClose={onClose}
      onSubmit={submit}
      titleText={titleText}
      submitButtonText={t('track.lookup.submit')}
      submitButtonDisabled={submitButtonDisabled}
      dialogClassName={`${styles.modal} ${
        isGridEmpty ? styles.compactEmptyModal : ''
      }`}
    >
      <div className={styles.lookupTrackModalBody}>
        <div className={styles.searchContainer}>
          <TextInput
            id="isrc-search"
            value={isrc}
            handleInput={(e) => setIsrc(e.target.value)}
            className={styles.isrcSearchInput}
            inputClassname="form-control"
            placeholder={t('track.isrc')}
            labelTitle={''}
            leadingIcon={
              <i
                className="icon search-icon far fa-search"
                aria-hidden="true"
              />
            }
          />
          <button
            type="button"
            className={`btn btn-secondary ${styles.searchButton}`}
            onClick={handleSearch}
            disabled={!trimmedIsrc || isFetching}
          >
            {t('product.productSearch.search')}
          </button>
        </div>

        <div
          className={`${styles.agGridContainer} ${
            isGridEmpty ? styles.compactAgGridContainer : ''
          } ag-theme-alpine`}
        >
          <AgGridReact
            ref={gridRef}
            rowData={visibleLookupResults}
            columnDefs={columnDefs}
            loading={isFetching}
            domLayout="autoHeight"
            rowSelection="single"
            suppressCellFocus={true}
            defaultColDef={DEFAULT_COL_DEF}
            autoSizeStrategy={FIT_GRID_WIDTH_AUTO_SIZE_STRATEGY}
            onSelectionChanged={handleSelectionChanged}
            overlayLoadingTemplate={loadingOverlayTemplate}
            overlayNoRowsTemplate={noRowsOverlayTemplate}
            theme="legacy"
            columnMenu="legacy"
          />
        </div>
      </div>
    </FormModal>
  );
}
function renderGridOverlay(text: string, type: 'loading' | 'no-rows') {
  return `<span class="ag-overlay-${type}-center">${text}</span>`;
}

function useColumnDefs() {
  const { t } = useTranslation();
  const columnDefs = useMemo<ColDef<ITrackLookupResult>[]>(
    () => [
      {
        field: 'productType',
        headerName: t('track.lookup.productType'),
        width: 120,
        cellRenderer: (params: any) => {
          return (
            <div className="badge format-badge badge-default">
              {getProductType(params.data)}
            </div>
          );
        },
      },
      {
        field: 'trackTitle',
        headerName: t('track.lookup.trackTitle'),
        width: 105,
      },
      {
        field: 'displayArtist',
        headerName: t('track.displayArtist'),
        width: 125,
      },
      { field: 'isrc', headerName: t('track.isrc'), width: 90 },
      {
        field: 'productTitle',
        headerName: t('track.lookup.productTitle'),
        width: 130,
      },
      {
        field: 'upc',
        headerName: t('product.editProduct.GTIN'),
        width: 100,
        cellRenderer: (params: any) => {
          const url = getProductUrl(params.data);

          if (!params.value || !url) {
            return params.value;
          }

          return (
            <a href={url} target="_blank" rel="noreferrer">
              {params.value}
            </a>
          );
        },
      },
      {
        field: 'condition',
        headerName: t('product.editProduct.albumState'),
        minWidth: 95,
        flex: 1,
        cellRenderer: (params: any) => {
          if (!params.value) return null;
          return (
            <span
              className={`badge state-badge ${getAlbumStateBadgeClass(params.value)}`}
            >
              {t(`product.${params.value}`, { defaultValue: params.value })}
            </span>
          );
        },
      },
    ],
    [t]
  );

  return columnDefs;
}

function getProductType(album: ITrackLookupResult) {
  switch (album.source?.toLowerCase()) {
    case 'digital':
      return album.productType
        ? i18n.t(
            `common.digitalProductTypeShort.${album.productType}`,
            album.productType
          )
        : album.productType;
    case 'physical':
      return translatePhysicalProductType(album.productType ?? null);
  }
}

function getProductUrl(album?: ITrackLookupResult) {
  const productId = album?.productId;

  if (!album || !productId) {
    return null;
  }

  switch (album.source?.toLowerCase()) {
    case 'digital':
      return album.productType
        ? getCmsProductUrl(productId, album?.productType)
        : null;
    case 'physical':
      return `/Physical/EditProduct/Album/${productId}`;
    default:
      return null;
  }
}

function filterCurrentTrackFromLookupResults(
  lookupResults: ITrackLookupResult[] | undefined,
  track: ITrack
) {
  return (
    lookupResults?.filter(
      (lookupResult) => !isCurrentTrackLookupResult(lookupResult, track)
    ) ?? []
  );
}

function isCurrentTrackLookupResult(
  lookupResult: ITrackLookupResult,
  track: ITrack
) {
  if (lookupResult.source?.toLowerCase() !== 'physical') {
    return false;
  }

  if (
    track.id &&
    lookupResult.trackId &&
    lookupResult.trackId === String(track.id)
  ) {
    return true;
  }

  return (
    !!lookupResult.productId &&
    !!track.albumId &&
    lookupResult.productId === String(track.albumId) &&
    lookupResult.discNumber === track.discOrSideNumber &&
    lookupResult.trackNumber === track.trackNumber
  );
}
